import { useTranslation } from 'react-i18next';
import { useUpdateClinician } from '@/hooks/queries';
import { useAuthStore } from '@/auth';
import { clinicianHomeHospitalId } from '@/api/clinicianHospital';
import { LANGUAGES, resolveLanguageCode, type LanguageCode } from './languages';

/**
 * Read + change the clinician's interface language.
 *
 * Persistence happens in two places:
 * 1. The `i18n` init module listens for `languageChanged` and writes to
 *    `localStorage["i18nextLng"]` + the `i18next` cookie, so the choice
 *    survives reloads on the same device immediately.
 * 2. We also `PATCH /hospitals/{h}/clinicians/{c}` with `{locale}` so the
 *    profile is authoritative — matches v1 and lets the choice follow the
 *    user to a new device. Fire-and-forget: if the PATCH fails the local
 *    cookie still holds, and `useUserDefaultLanguage` won't overwrite it
 *    because the profile fetch returns the *previous* locale, which is what
 *    we just changed away from on the client.
 *
 * Extracted from the old header `LanguageSwitcher` so the sidebar
 * Preferences menu owns the UI without owning the persistence rules.
 */
export const useLanguagePreference = () => {
  const { i18n } = useTranslation();
  const current = resolveLanguageCode(i18n.language);
  const clinician = useAuthStore((s) => s.clinician);
  const setClinician = useAuthStore((s) => s.setClinician);
  const updateClinician = useUpdateClinician();

  const choose = (code: LanguageCode) => {
    if (code === current) return;
    i18n.changeLanguage(code);
    // Optimistically update the auth-store clinician so a profile re-fetch
    // doesn't briefly flip the language back via useUserDefaultLanguage
    // before the PATCH lands.
    if (!clinician?.id) return;
    const hospitalId = clinicianHomeHospitalId(clinician);
    setClinician({ ...clinician, locale: code });
    if (hospitalId == null) return;
    updateClinician.mutate({
      hospitalId,
      clinicianId: clinician.id,
      body: { locale: code },
    });
  };

  return { current, choose, languages: LANGUAGES };
};
